// src/components/Admin/RutasDidacticasComp/RutasDidacticasEditStep2.js
import React, { useState, useEffect, useCallback } from "react";
import { Loader, Image, Checkbox, Button, Icon } from "semantic-ui-react";
import {
  Avatar,
  Switch,
  Segmented,
  Button as AntBtn,
  Spin,
  Typography,
} from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { AssistanceApi, DidacticRoutesAPI } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { NEW_SALAS } from "../../../../ui/featureFlags";
import "./RutasDidacticasEditStep2.scss";
import "../RutasDidacticasForm/RutasStep.scss";

const assistanceApi = new AssistanceApi();
const didacticRoutesAPI = new DidacticRoutesAPI();

// ID fijo de Educa360 (puedes moverlo a constants)
const EDUCA360_USER_ID = "67921ab3740e0f2aba258111";

export function RutasDidacticasEditStep2({
  route,
  accessToken,
  onRefresh: _onRefresh, // <- renombrado para no usarlo aquí (evita lint)
  onClose,
  onBack,
  onNext, // avanzar a Paso 3
}) {
  const { t } = useTranslation("itineraries");
  const { user } = useAuth(); // user.user._id
  const [loading, setLoading] = useState(false);
  const [assistants, setAssistants] = useState([]);
  const [selectedAssistants, setSelectedAssistants] = useState([]);
  const [initialAssignedIds, setInitialAssignedIds] = useState([]);
  const [viewMode, setViewMode] = useState("edu360"); // "edu360" | "mine"
  // Si el guardado falla, el paso no avanza: sin esto el boton "Siguiente"
  // parecia averiado y el motivo solo estaba en la consola del navegador.
  const [saveError, setSaveError] = useState(null);

  const arraysEqual = (a, b) =>
    a.length === b.length && a.every((x) => b.includes(x));

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await assistanceApi.getAssistances(accessToken, 1, 100);
      let docs = response?.assistances?.docs || [];

      if (viewMode === "edu360")
        docs = docs.filter((a) => a.user === EDUCA360_USER_ID);
      else docs = docs.filter((a) => a.user === user.user._id);

      setAssistants(docs);

      const assignedIds = (route.assistants || []).map((a) => a.assistant);
      setSelectedAssistants(assignedIds);
      setInitialAssignedIds(assignedIds);
    } catch (error) {
      console.error("Error al cargar asistentes:", error);
    } finally {
      setLoading(false);
    }
  }, [accessToken, route.assistants, viewMode, user.user._id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleAssistant = (assistantId) => {
    setSelectedAssistants((prev) =>
      prev.includes(assistantId)
        ? prev.filter((id) => id !== assistantId)
        : [...prev, assistantId],
    );
  };

  // Guarda datos + asistentes (si cambiaron) y pasa a paso 3 (sin refrescar el parent)
  const handleSaveAndNext = async () => {
    if (!route?._id) return;
    setLoading(true);
    setSaveError(null);
    try {
      // 1) Persistir datos básicos del Step1 (si existen en "route")
      await didacticRoutesAPI.updateDidacticRoute(accessToken, route._id, {
        nameRute: route.nameRute,
        description: route.description,
        classnumber: route.classnumber,
      });

      // 2) Solo llama a API de asistentes si hubo cambios reales
      if (!arraysEqual(selectedAssistants, initialAssignedIds)) {
        await didacticRoutesAPI.insertAssistantsInRoute(
          accessToken,
          route._id,
          selectedAssistants,
        );
      }

      // ⚠️ No llamamos a onRefresh aquí: lo hará Step 3 al terminar.
      onNext?.(); // avanzar al Paso 3 (NO cerrar)
    } catch (error) {
      console.error("Error al actualizar el itinerario:", error);
      setSaveError(error?.message || t("editStep2.saveError"));
    } finally {
      setLoading(false);
    }
  };

  const handleViewEdu360 = () => setViewMode("edu360");
  const handleViewMine = () => setViewMode("mine");

  if (NEW_SALAS) {
    return (
      <div className="rutas-step rutas-step2">
        <Typography.Title level={4} className="rutas-step__title">
          {t("editStep2.title")}
        </Typography.Title>

        <Segmented
          value={viewMode}
          onChange={(v) => setViewMode(v)}
          options={[
            { label: t("editStep2.educa360Button"), value: "edu360" },
            { label: t("editStep2.ownButton"), value: "mine" },
          ]}
          style={{ marginBottom: 14 }}
        />

        {loading ? (
          <div
            style={{ display: "flex", justifyContent: "center", padding: 24 }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <div>
            {assistants.map((assistant) => {
              const isChecked = selectedAssistants.includes(assistant._id);
              return (
                <div
                  key={assistant._id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 4px",
                    borderBottom: "1px solid #eef1f5",
                  }}
                >
                  <Avatar src={assistant.miniature} size={40} />
                  <span style={{ flex: 1, fontWeight: 600, minWidth: 0 }}>
                    {assistant.title}
                  </span>
                  <Switch
                    checked={isChecked}
                    onChange={() => handleToggleAssistant(assistant._id)}
                  />
                </div>
              );
            })}
          </div>
        )}

        {saveError && (
          <Typography.Text
            type="danger"
            style={{ display: "block", marginTop: 12 }}
          >
            {saveError}
          </Typography.Text>
        )}

        <div className="rutas-step__footer">
          <AntBtn size="large" onClick={onBack} disabled={loading}>
            {t("editStep2.backButton")}
          </AntBtn>
          <AntBtn size="large" onClick={onClose} disabled={loading}>
            {t("editStep2.cancelButton")}
          </AntBtn>
          <AntBtn
            type="primary"
            size="large"
            icon={<RightOutlined />}
            loading={loading}
            onClick={handleSaveAndNext}
          >
            {t("editStep2.nextButton")}
          </AntBtn>
        </div>
      </div>
    );
  }

  return (
    <div className="rutas-step2">
      <h3 className="rutas-step2__title">{t("editStep2.title")}</h3>

      <div className="rutas-step2__filter-buttons">
        <Button
          onClick={handleViewEdu360}
          color={viewMode === "edu360" ? "blue" : undefined}
          className="rutas-step2__filter-btn"
        >
          {t("editStep2.educa360Button")}
        </Button>
        <Button
          onClick={handleViewMine}
          color={viewMode === "mine" ? "grey" : undefined}
          className="rutas-step2__filter-btn"
        >
          {t("editStep2.ownButton")}
        </Button>
      </div>

      {loading ? (
        <div className="rutas-step2__loader-wrapper">
          <Loader active inline="centered" size="large" />
        </div>
      ) : (
        <div className="rutas-step2__assistants-list">
          {assistants.map((assistant) => {
            const isChecked = selectedAssistants.includes(assistant._id);
            return (
              <div key={assistant._id} className="rutas-step2__assistant-row">
                <Image
                  src={assistant.miniature}
                  size="tiny"
                  circular
                  className="rutas-step2__assistant-img"
                />
                <div className="rutas-step2__assistant-info">
                  <strong className="rutas-step2__assistant-title">
                    {assistant.title}
                  </strong>
                </div>
                <Checkbox
                  toggle
                  checked={isChecked}
                  onChange={() => handleToggleAssistant(assistant._id)}
                  className="rutas-step2__assistant-checkbox"
                />
              </div>
            );
          })}
        </div>
      )}

      {saveError && (
        <p className="rutas-step2__error" style={{ color: "#cf1322" }}>
          {saveError}
        </p>
      )}

      <div className="rutas-step2__buttons">
        <Button onClick={onBack} color="grey" className="rutas-step2__btn">
          {t("editStep2.backButton")}
        </Button>

        <Button
          primary
          onClick={handleSaveAndNext}
          loading={loading}
          disabled={loading}
          className="rutas-step2__btn"
        >
          <Icon name="arrow right" />
          {t("editStep2.nextButton")}
        </Button>

        <Button
          onClick={onClose}
          disabled={loading}
          className="rutas-step2__btn"
        >
          {t("editStep2.cancelButton")}
        </Button>
      </div>
    </div>
  );
}
